import React, { useState } from 'react';
import { X, Plus, BarChart3, Calendar, TrendingUp } from 'lucide-react';
import { Metric } from '../../types';
import { apiService } from '../../services/api';

interface RecordMetricValueModalProps {
  metric: Metric;
  onClose: () => void;
  onSuccess: () => void;
}

export const RecordMetricValueModal: React.FC<RecordMetricValueModalProps> = ({
  metric,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    value: '',
    recordedDate: new Date().toISOString().split('T')[0],
    note: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.value.trim()) {
      setError('Value is required');
      return;
    }

    // For binary metrics, convert checkbox state to 1/0
    let valueToSubmit: number;
    if (metric.metricType === 'binary') {
      valueToSubmit = formData.value === 'true' ? 1 : 0;
    } else {
      valueToSubmit = parseFloat(formData.value);
      if (isNaN(valueToSubmit)) {
        setError('Please enter a valid number');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      await apiService.recordMetricValue(metric.id, {
        value: valueToSubmit,
        recordedDate: formData.recordedDate,
        note: formData.note.trim() || undefined
      });

      onSuccess();
    } catch (err) {
      console.error('Recording metric value failed:', err);
      setError('Failed to record metric value');
    } finally {
      setLoading(false);
    }
  };

  const renderValueInput = () => {
    switch (metric.metricType) {
      case 'binary':
        return (
          <div className="flex items-center space-x-3">
            <label className="flex items-center">
              <input
                type="radio"
                name="binaryValue"
                value="true"
                checked={formData.value === 'true'}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="mr-2 text-green-600 focus:ring-green-500"
              />
              <span className="text-green-700 font-medium">Completed / Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="binaryValue"
                value="false"
                checked={formData.value === 'false'}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="mr-2 text-red-600 focus:ring-red-500"
              />
              <span className="text-red-700 font-medium">Not Done / No</span>
            </label>
          </div>
        );
      
      case 'rate':
        return (
          <div className="space-y-2">
            <input
              type="number"
              step="0.01"
              required
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              className="input-field"
              placeholder={`Enter rate value${metric.unit ? ` (${metric.unit})` : ''}`}
            />
            <p className="text-xs text-gray-500">
              Enter the rate for this time period
            </p>
          </div>
        );
      
      default: // absolute
        return (
          <input
            type="number"
            step="0.01"
            required
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            className="input-field"
            placeholder={`Enter value${metric.unit ? ` (${metric.unit})` : ''}`}
          />
        );
    }
  };

  const getProgressColor = () => {
    if (!metric.targetValue) return 'bg-gray-400';
    const current = parseFloat(formData.value) || metric.currentValue;
    const percentage = (current / metric.targetValue) * 100;
    
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const calculateNewProgress = () => {
    if (!metric.targetValue) return 0;
    const newValue = metric.metricType === 'binary' 
      ? (formData.value === 'true' ? 1 : 0)
      : parseFloat(formData.value) || 0;
    return Math.min((newValue / metric.targetValue) * 100, 100);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Plus className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Record Value
                </h2>
                <p className="text-sm text-gray-500">
                  {metric.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Current Status */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Current Value</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {metric.currentValue}
                    {metric.unit && <span className="text-sm font-normal"> {metric.unit}</span>}
                  </span>
                </div>
                
                {metric.targetValue && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((metric.currentValue / metric.targetValue) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600">
                      {Math.round((metric.currentValue / metric.targetValue) * 100)}%
                    </span>
                  </div>
                )}
              </div>

              {/* Value Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Value *
                  {metric.metricType === 'binary' ? (
                    <span className="text-gray-500 font-normal"> (Select status)</span>
                  ) : (
                    metric.unit && <span className="text-gray-500 font-normal"> ({metric.unit})</span>
                  )}
                </label>
                {renderValueInput()}
              </div>

              {/* Preview Progress */}
              {formData.value && metric.targetValue && metric.metricType !== 'binary' && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Progress Preview</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-blue-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                        style={{ width: `${calculateNewProgress()}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-blue-900">
                      {Math.round(calculateNewProgress())}%
                    </span>
                  </div>
                </div>
              )}

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    required
                    value={formData.recordedDate}
                    onChange={(e) => setFormData({ ...formData, recordedDate: e.target.value })}
                    className="input-field pl-10"
                  />
                </div>
              </div>

              {/* Optional Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note (Optional)
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={3}
                  className="input-field"
                  placeholder="Add any context or notes about this measurement..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                {loading ? 'Recording...' : 'Record Value'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};